// src/componentes/Propiedades/NuevaPropiedad.jsx
import React, { useState, useEffect } from "react";
import { addDoc, collection, Timestamp, doc, getDoc, getDocs } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db } from "../../Firebase/firebaseconfig";
import { nextFolioPropiedad } from "../../helpers/folio";
import { buildPropDoc } from "../../helpers/propiedad";
import { listProyectos } from "../../services/proyectosService";
import { buildPropSearchFields } from "../../helpers/propIndex";
import notify from "../common/notify";

export default function NuevaPropiedad() {
  const navigate = useNavigate();

  // Form unificado (campos antiguos + nuevos)
  const [form, setForm] = useState({
    nombre: "",
    tipo: "Terreno",
    fraccionamientoId: "",
    fraccNombre: "", // nombre del fraccionamiento seleccionado
    manzana: "",
    lote: "",
    ubicacion: "",
    metros2: "",
    precioContado: "",
    precioFinanciado: "",
    proyectoId: "",
    proyectoNombre: "",
    notas: "",
  });

  const [previewFolio, setPreviewFolio] = useState("Cargando...");
  const [saving, setSaving] = useState(false);
  const [fraccionamientos, setFraccionamientos] = useState([]);
  const [proyectos, setProyectos] = useState([]);

  // --- Cargar fraccionamientos para el selector ---
  useEffect(() => {
    const fetchFraccs = async () => {
      try {
        const snap = await getDocs(collection(db, "fraccionamientos"));
        setFraccionamientos(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error("Error cargando fraccionamientos:", e);
      }
    };
    fetchFraccs();
  }, []);

  useEffect(() => {
    const fetchProyectos = async () => {
      try {
        const data = await listProyectos();
        setProyectos(data);
      } catch (e) {
        console.error("Error cargando proyectos:", e);
      }
    };
    fetchProyectos();
  }, []);
  // --- Preview del proximo folio (NO incrementa el contador) ---
  useEffect(() => {
    const fetchNext = async () => {
      try {
        const counterRef = doc(db, "counters", "propiedades"); // "counters" (no "contadores")
        const snap = await getDoc(counterRef);
        if (!snap.exists()) {
          const next = 1, prefix = "PR", pad = 5;
          setPreviewFolio(`${prefix}-${String(next).padStart(pad, "0")}`);
          return;
        }
        const data = snap.data();
        const seq = Number(data.seq || 0);
        const next = seq + 1;
        const prefix = data.prefix ?? "PR";
        const pad = data.pad ?? 5;
        setPreviewFolio(`${prefix}-${String(next).padStart(pad, "0")}`);
      } catch (err) {
        console.error("Error leyendo contador de folios:", err);
        setPreviewFolio("ERROR");
      }
    };
    fetchNext();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    // Validaciones minimas
    const req = ["tipo", "ubicacion", "precioContado", "precioFinanciado"];
    for (const k of req) {
      if (!String(form[k] ?? "").trim()) {
        notify.error("Completa todos los campos obligatorios.");
        return;
      }
    }

    // ── Validación coherencia precio (ES2-3) ─────────────────────────
    const pc = Number(form.precioContado || 0);
    const pf = Number(form.precioFinanciado || 0);
    if (pc <= 0 || pf <= 0) {
      notify.error("Los precios deben ser mayores a cero.");
      return;
    }
    if (pc > pf) {
      notify.error(
        `Precio inconsistente: precio contado ($${pc.toLocaleString()}) no puede ser MAYOR que precio financiado ($${pf.toLocaleString()}). Revísalos.`,
      );
      return;
    }

    setSaving(true);
    try {
      // 1) Folio maestro (transaccional)
      const { folio } = await nextFolioPropiedad();

      // 2) Asegurar nombre del fraccionamiento (clave para n-grams)
      let fraccNombre = form.fraccNombre || "";
      if (form.fraccionamientoId && !fraccNombre) {
        try {
          const fraccSnap = await getDoc(doc(db, "fraccionamientos", form.fraccionamientoId));
          if (fraccSnap.exists()) fraccNombre = fraccSnap.data().nombre || "";
        } catch (e) {
          console.warn("No se pudo leer fraccionamiento para nombre:", e);
        }
      }

      // 3) Documento base para helpers
      const base = {
        ...form,
        folio,
        fraccNombre,                       // compat con tu buscador
        fraccionamientoNombre: fraccNombre // si en otros lados usas este nombre
      };

      // 4) buildPropDoc (tu helper actual: claveLote, codigoHumano, ints, etc.)
      const built = buildPropDoc(base);

      // 5) Campos finales + metadatos
      const docData = {
        folio,
        ...built,
        nombre: form.nombre || "",
        notas: form.notas || "",
        creado: Timestamp.now(),
        actualizado: Timestamp.now(),
        disponible: true,
        activo: true,
        asociadaADeuda: false,
        bitacora: [
          {
            ts: Timestamp.now(),
            tipo: "creacion",
            detalle: `Alta ${folio}`,
            userId: null, // auth?.currentUser?.uid si aplica
          },
        ],
        // reflejar tambien los nombres para consistencia en UI
        fraccNombre,
        fraccionamientoNombre: fraccNombre,
        proyectoId: form.proyectoId || "",
        proyectoNombre: form.proyectoNombre || "",
      };

      // Normalizaciones numericas
      docData.metros2 = Number(form.metros2 || 0);
      docData.precioContado = Number(form.precioContado || 0);
      docData.precioFinanciado = Number(form.precioFinanciado || 0);

      // 6) Indices de busqueda (n-grams/tokens) ANTES de guardar
      const idx = buildPropSearchFields({
        ...docData,
        // por si buildPropDoc genero campos como codigoHumano/claveLote
        // los incluimos en el calculo del indice
      });

      // 7) Alta (una sola escritura con todo)
      const ref = await addDoc(collection(db, "propiedades"), {
        ...docData,
        ...idx,
      });

      // 8) Navega al detalle
      navigate(`/app/propiedades/${ref.id}`);
    } catch (err) {
      console.error("Error al crear propiedad:", err);
      notify.error("Error al crear propiedad: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="nuevo-cliente-container">
      <h2>Registrar Nueva Propiedad</h2>

      <form onSubmit={onSubmit} className="form-cliente">
        {/* Folio preview (solo lectura) */}
        <input value={previewFolio} disabled placeholder="Folio (previo)" />

        {/* Nombre descriptivo (opcional) */}
        <input
          name="nombre"
          placeholder="Nombre (opcional)"
          value={form.nombre}
          onChange={handleChange}
        />

        {/* Tipo */}
        <input
          name="tipo"
          placeholder="Tipo (ej. Terreno, Casa)"
          value={form.tipo}
          onChange={handleChange}
          required
        />

        {/* Fraccionamiento (selector) */}
        <div className="field">
          <select
            value={form.fraccionamientoId ? `${form.fraccionamientoId}|${form.fraccNombre}` : ""}
            onChange={(e) => {
              const [id, nombre] = e.target.value.split("|");
              const fracc = fraccionamientos.find((f) => f.id === id);
              setForm((prev) => ({
                ...prev,
                fraccionamientoId: id || "",
                fraccNombre: nombre || "",
                proyectoId: fracc?.proyectoId || prev.proyectoId,
                proyectoNombre: fracc?.proyectoNombre || prev.proyectoNombre,
              }));
            }}
          >
            <option value="">-- Selecciona fraccionamiento --</option>
            {fraccionamientos.map((f) => (
              <option key={f.id} value={`${f.id}|${f.nombre}`}>
                {f.nombre} - {f.ubicacion}
              </option>
            ))}
          </select>
        </div>

        {/* Manzana / Lote */}
        <div style={{ display: "flex", gap: 8 }}>
          <input
            name="manzana"
            type="number"
            placeholder="Manzana"
            value={form.manzana}
            onChange={handleChange}
          />
          <input
            name="lote"
            type="number"
            placeholder="Lote"
            value={form.lote}
            onChange={handleChange}
          />
        </div>

        {/* Ubicacion */}
        <input
          name="ubicacion"
          placeholder="Ubicacion"
          value={form.ubicacion}
          onChange={handleChange}
          required
        />

        <div className="field">
          <select
            value={form.proyectoId ? `${form.proyectoId}|${form.proyectoNombre}` : ""}
            onChange={(e) => {
              const [id, nombre] = e.target.value.split("|");
              setForm((prev) => ({
                ...prev,
                proyectoId: id || "",
                proyectoNombre: nombre || "",
              }));
            }}
          >
            <option value="">-- Proyecto relacionado (opcional) --</option>
            {proyectos.map((p) => (
              <option key={p.id} value={`${p.id}|${p.nombre}`}>
                {p.nombre}
              </option>
            ))}
          </select>
        </div>

        {/* m2 */}
        <input
          name="metros2"
          type="number"
          placeholder="Metros cuadrados"
          value={form.metros2}
          onChange={handleChange}
        />

        {/* Precios */}
        <input
          name="precioContado"
          type="number"
          placeholder="Precio contado (MXN)"
          value={form.precioContado}
          onChange={handleChange}
          required
        />
        <input
          name="precioFinanciado"
          type="number"
          placeholder="Precio financiado (MXN)"
          value={form.precioFinanciado}
          onChange={handleChange}
          required
        />

        {/* Notas */}
        <textarea
          name="notas"
          placeholder="Notas adicionales"
          value={form.notas}
          onChange={handleChange}
        />

        <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
          <button type="submit" disabled={saving}>
            {saving ? "Guardando..." : "Guardar Propiedad"}
          </button>
          <button
            type="button"
            onClick={() => navigate(-1)}
            disabled={saving}
            style={{
              background: "#f1f5f9",
              color: "#1e293b",
              border: "1px solid #cbd5e1",
            }}
          >
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
}

